import { DayType, PrayerType, ScheduleEntry } from '../types';
import { addDays, DayFacts, eveningBelongsToNextDay, getDayFacts } from './jewishCalendar';

/**
 * Which entries a prayer uses on a date (docs/day-types-spec.md, sections 2–4).
 *
 * A prayer looks through a list of levels, highest priority first. The first
 * level with at least one matching entry wins. A level marked `withWeekday`
 * (Chanukah, Rosh Chodesh, Mon/Thu) also keeps the weekday entries it does not
 * replace (`overridesEntryId`). When no level matches, there is no minyan:
 * that is what "must be set" means.
 */
export interface Level {
  tags: DayType[];
  withWeekday?: boolean;
}

/** 'day' = a daytime prayer; 'start' / 'end' = the evening that starts / ends a day. */
export type Part = 'day' | 'start' | 'end';

export interface PrayerPlan {
  part: Part;
  levels: Level[];
}

const L = (...tags: DayType[]): Level => ({ tags });

/** Which part of the day an entry is for (see ScheduleEntry.night). */
export function entryPart(entry: Pick<ScheduleEntry, 'prayerType' | 'night'>): Part {
  if (entry.prayerType === 'kabalatShabbat' || entry.prayerType === 'tefilahZakha') return 'start';
  if (entry.prayerType === 'maariv') return entry.night ?? 'end';
  if (NIGHT_OR_DAY.includes(entry.prayerType)) return entry.night === 'start' ? 'start' : 'day';
  return 'day';
}

/** Evening prayers. Hakafot and Megillah are evening only for entries marked night 'start'. */
export const NIGHT_OR_DAY: PrayerType[] = ['hakafot', 'megillah'];
export const NIGHT_PRAYERS: PrayerType[] = ['maariv', 'kabalatShabbat', 'tefilahZakha'];

/** These prayers exist only on the night that starts a day. */
const START_ONLY: PrayerType[] = ['kabalatShabbat', 'tefilahZakha'];

function yomTovLevels(f: DayFacts): Level[] {
  return [
    ...(f.roshHashanaOnFriday ? [L('roshHashanaFriday')] : []),
    ...(f.yomTovOnFriday ? [L('yomTovFriday')] : []),
    ...(f.specificYomTov ? [L(f.specificYomTov)] : []),
    L('yomTov'),
  ];
}

/** The ordinary-day levels: Chanukah, Rosh Chodesh, Mon/Thu (each over weekday), then weekday. */
function weekdayFamily(f: DayFacts, opts: { mondayThursday?: boolean; chanukah?: boolean } = {}): Level[] {
  const levels: Level[] = [];
  if (opts.chanukah !== false && f.chanukahDay) levels.push({ tags: ['chanukah'], withWeekday: true });
  if (f.roshChodesh) levels.push({ tags: ['roshChodesh'], withWeekday: true });
  if (f.beinHazmanim) levels.push({ tags: ['beinHazmanim'], withWeekday: true });
  if (opts.mondayThursday !== false && f.mondayOrThursday) {
    levels.push({ tags: ['sheniHamishi'], withWeekday: true });
  }
  levels.push(L('weekday'));
  return levels;
}

function dayLevels(prayer: PrayerType, f: DayFacts): Level[] {
  if (prayer === 'neila') return f.yomKippur ? [L('yomKippur')] : [];
  if (prayer === 'yizkor') {
    if (!f.yizkor) return [];
    return f.yomKippur ? [L('yomKippur')] : yomTovLevels(f);
  }
  if (prayer === 'musaf') {
    if (f.yomKippur) return [L('yomKippur')];
    if (f.yomTov) return yomTovLevels(f);
    if (f.shabbos) return [L('shabbos')];
    // Not set up on these days (part of Shacharit), kept for entries made before.
    if (f.cholHaMoed) return [L('cholHaMoed')];
    if (f.roshChodesh) return [L('roshChodesh')];
    return [];
  }
  if (prayer === 'hakafot') return f.yomTov ? yomTovLevels(f) : [];
  // No shofar on Shabbos (Rosh Hashana day 1 on Shabbos).
  if (prayer === 'tekiot') return f.specificYomTov === 'roshHashana' && !f.shabbos ? yomTovLevels(f) : [];
  if (prayer === 'megillah') return f.purim ? [...(f.erevShabbos ? [L('purimFriday')] : []), L('purim')] : [];

  // Shacharit, Mincha (and any other daytime prayer).
  const isShacharit = prayer === 'shacharit';
  const isMincha = prayer === 'mincha';
  if (f.yomKippur) return [L('yomKippur')];
  if (f.tishaBav) return [L('tishaBav')];
  // Yom Tov on Friday: Mincha has its own time, which must be set (spec section 4).
  if (f.yomTov && isMincha && f.roshHashanaOnFriday) return [L('roshHashanaFriday')];
  if (f.yomTov && isMincha && f.yomTovOnFriday) return [L('yomTovFriday')];
  if (f.yomTov) return yomTovLevels(f);
  if (f.purim) return [...(f.erevShabbos ? [L('purimFriday')] : []), L('purim')];
  if (f.minorFast) {
    const fast = [...(f.erevShabbos ? [L('fastFriday')] : []), L('fastMinor')];
    if (!isShacharit) return fast;
    return [...fast, ...(f.erevShabbos ? [L('erevShabbos')] : []), ...weekdayFamily(f, { mondayThursday: false })];
  }
  if (f.erevYomKippur) return [L('erevYomKippur')];
  if (f.shabbos) {
    if (isMincha && f.erevTishaBav) return [L('erevTishaBav'), L('shabbos')];
    return [L('shabbos')];
  }
  if (f.cholHaMoed) {
    if (isShacharit) {
      return [
        ...(f.hoshanaRabba ? [L('hoshanaRabba')] : []),
        L('cholHaMoed'),
        ...(f.erevYomTov ? [L('erevYomTov')] : []),
        ...(f.erevShabbos ? [L('erevShabbos')] : []),
        ...weekdayFamily(f, { mondayThursday: false }),
      ];
    }
    if (isMincha) {
      // Hoshana Rabba and Pesach day 6: Mincha is Erev Yom Tov's.
      if (f.erevYomTov) return [L('erevYomTov'), ...(f.erevShabbos ? [L('erevShabbos')] : [])];
      // A Chol HaMoed Friday: the Friday Mincha first (it must be before Shabbos).
      if (f.erevShabbos) return [L('erevShabbos'), L('cholHaMoed')];
    }
    return [L('cholHaMoed'), ...weekdayFamily(f, { mondayThursday: false })];
  }
  if (f.erevYomTov) {
    const friday = f.erevShabbos ? [L('erevShabbos')] : [];
    if (isMincha) return [L('erevYomTov'), ...friday];
    return [L('erevYomTov'), ...friday, ...weekdayFamily(f)];
  }
  if (isMincha && f.erevTishaBav) return [L('erevTishaBav')];
  if (f.erevShabbos) {
    if (isMincha) return [...(f.chanukahDay ? [L('chanukahFriday')] : []), L('erevShabbos')];
    if (isShacharit) {
      // Chanukah wins over Friday; a Friday Shacharit wins over Rosh Chodesh.
      const chanukah: Level[] = f.chanukahDay ? [{ tags: ['chanukah'], withWeekday: true }] : [];
      return [...chanukah, L('erevShabbos'), ...weekdayFamily(f, { chanukah: false })];
    }
    return [L('erevShabbos')];
  }
  return weekdayFamily(f, { mondayThursday: isShacharit || prayer === 'selichot' });
}

/** The night that starts day `n` (said on the evening of day `d`). */
function startLevels(prayer: PrayerType, d: DayFacts, n: DayFacts): Level[] {
  if (n.yomKippur) return [L('yomKippur')];
  if (n.tishaBav) return [L('tishaBav')];
  if (n.yomTov) return yomTovLevels({ ...n, roshHashanaOnFriday: false, yomTovOnFriday: false });
  if (n.purim) return [L('purim')];
  if (n.shabbos) {
    const special: Level[] = [
      ...(d.purim ? [L('purimFriday')] : []),
      ...(d.minorFast ? [L('fastFriday')] : []),
      ...(d.chanukahDay ? [L('chanukahFriday')] : []),
      ...(d.roshHashanaOnFriday ? [L('roshHashanaFriday')] : []),
      ...(d.yomTovOnFriday ? [L('yomTovFriday')] : []),
    ];
    // Yom Tov on Friday: no Kabbalat Shabbat unless the shul set one for that Friday.
    if (d.yomTov && prayer === 'kabalatShabbat') return special;
    return [...special, L('shabbos')];
  }
  return [];
}

/** The night after day `d` (its next day is an ordinary day). */
function endLevels(d: DayFacts): Level[] {
  if (d.yomKippur) return [L('yomKippur')];
  if (d.yomTov) return yomTovLevels({ ...d, roshHashanaOnFriday: false, yomTovOnFriday: false });
  if (d.shabbos) return [...(d.chanukahDay ? [L('chanukahShabbos')] : []), L('shabbos')];
  if (d.purim) return [L('purim')];
  if (d.minorFast) return [L('fastMinor')];
  if (d.tishaBav) return [L('tishaBav'), ...weekdayFamily(d, { mondayThursday: false })];
  if (d.cholHaMoed) return [L('cholHaMoed'), ...weekdayFamily(d, { mondayThursday: false })];
  return weekdayFamily(d, { mondayThursday: false });
}

/**
 * Where a prayer looks on a date. `night` is only for Hakafot / Megillah ('start' = at night).
 */
export function planPrayer(prayer: PrayerType, date: string, night?: 'start'): PrayerPlan {
  const f = getDayFacts(date);
  const isNight = NIGHT_PRAYERS.includes(prayer) || (NIGHT_OR_DAY.includes(prayer) && night === 'start');
  if (!isNight) return { part: 'day', levels: dayLevels(prayer, f) };
  if (eveningBelongsToNextDay(date)) {
    return { part: 'start', levels: startLevels(prayer, f, getDayFacts(addDays(date, 1))) };
  }
  if (START_ONLY.includes(prayer) || NIGHT_OR_DAY.includes(prayer)) return { part: 'end', levels: [] };
  return { part: 'end', levels: endLevels(f) };
}

export type SelichotFrom = 'elul2' | 'firstNight';

export interface SelichotPlan {
  /** Levels for entries that start on 2 Elul / on the first night. */
  levels: Record<SelichotFrom, Level[]>;
}

/**
 * Selichos (spec section 5). Empty set-ups fall back: "before Rosh Hashana"
 * covers Erev Rosh Hashana; "Aseret Yemei Teshuva" covers Tzom Gedalia and
 * Erev Yom Kippur, and itself falls back to "before Rosh Hashana". The first
 * night (Ashkenaz) has no fallback: its time is after midnight, unlike the mornings.
 * Never on Shabbos, Yom Tov or Tisha B'Av.
 */
export function planSelichot(date: string): SelichotPlan {
  const f = getDayFacts(date);
  const none: SelichotPlan = { levels: { elul2: [], firstNight: [] } };
  if (f.selichotFirstNight) return { levels: { elul2: [], firstNight: [L('selichotFirstNight')] } };
  if (f.shabbos || f.yomTov || f.yomKippur || f.tishaBav) return none;

  let season: Level[] = [];
  if (f.erevYomKippur) season = [L('erevYomKippur'), L('aseretYemeiTeshuva'), L('selichotBeforeRH')];
  else if (f.minorFast === 'tzomGedalia') season = [L('tzomGedalia'), L('aseretYemeiTeshuva'), L('selichotBeforeRH')];
  else if (f.aseretYemeiTeshuva) season = [L('aseretYemeiTeshuva'), L('selichotBeforeRH')];
  else if (f.erevRoshHashana) season = [L('erevRoshHashana'), L('selichotBeforeRH')];
  else if (f.elulBeforeErevRH) season = [L('selichotBeforeRH')];
  if (season.length) {
    // Entries made before Phase 6 (tagged with ordinary day types) still show in season.
    season = [...season, L(...getMinyanDayTags(date).filter((t) => t !== 'aseretYemeiTeshuva'))];
  }

  const extra: Level[] = [];
  if (f.minorFast && f.minorFast !== 'tzomGedalia') extra.push(L('fastMinor'));
  if (f.behab) extra.push(L('behab'));

  return {
    levels: {
      elul2: f.sefardiSelichotSeason ? season : extra,
      firstNight: f.ashkenazSelichotSeason ? season : extra,
    },
  };
}

/**
 * Every tag that applies to a date, highest priority first. Used for Selichos
 * (until step 4) and labels.
 */
export function getMinyanDayTags(date: string): DayType[] {
  const f = getDayFacts(date);
  const tags: DayType[] = [];
  const add = (cond: unknown, ...t: DayType[]) => { if (cond) tags.push(...t); };
  add(f.yomKippur, 'yomKippur');
  add(f.tishaBav, 'tishaBav');
  add(f.specificYomTov, f.specificYomTov as DayType);
  add(f.yomTov, 'yomTov');
  add(f.roshHashanaOnFriday, 'roshHashanaFriday');
  add(f.yomTovOnFriday, 'yomTovFriday');
  add(f.purim, 'purim');
  add(f.purim && f.erevShabbos, 'purimFriday');
  add(f.minorFast, 'fastMinor');
  add(f.minorFast && f.erevShabbos, 'fastFriday');
  add(f.erevYomKippur, 'erevYomKippur');
  add(f.erevTishaBav, 'erevTishaBav');
  add(f.shabbos, 'shabbos');
  add(f.shabbos && f.chanukahDay, 'chanukahShabbos');
  add(f.cholHaMoed, 'cholHaMoed');
  add(f.hoshanaRabba, 'hoshanaRabba');
  add(f.erevYomTov, 'erevYomTov');
  add(f.erevShabbos, 'erevShabbos');
  add(f.erevShabbos && f.chanukahDay, 'chanukahFriday');
  add(f.chanukahDay, 'chanukah');
  add(f.roshChodesh && !f.yomTov, 'roshChodesh');
  add(f.behab, 'behab');
  add(f.beinHazmanim, 'beinHazmanim');
  add(f.aseretYemeiTeshuva, 'aseretYemeiTeshuva');
  const special = f.yomKippur || f.tishaBav || f.yomTov || f.purim || f.minorFast ||
    f.erevYomKippur || f.shabbos || f.cholHaMoed || f.erevYomTov || f.erevShabbos;
  add(!special && f.mondayOrThursday, 'sheniHamishi');
  add(!special, 'weekday');
  return tags;
}

/**
 * The labels the public sees for a date, most important first, Shabbos first
 * (decided by Yoel, October 4, 2026). Internal set-up tags (special Fridays,
 * Selichos, Mon/Thu) are not labels; "Weekday" only when there is nothing else.
 */
export function getDayLabels(date: string): DayType[] {
  const f = getDayFacts(date);
  const labels: DayType[] = [];
  const add = (cond: unknown, t: DayType) => { if (cond) labels.push(t); };
  add(f.shabbos, 'shabbos');
  add(f.yomKippur, 'yomKippur');
  add(f.yomTov, f.specificYomTov ?? 'yomTov');
  add(f.tishaBav, 'tishaBav');
  add(f.purim, 'purim');
  add(f.minorFast, 'fastMinor');
  add(f.hoshanaRabba, 'hoshanaRabba');
  add(f.cholHaMoed && !f.hoshanaRabba, 'cholHaMoed');
  add(f.erevYomKippur, 'erevYomKippur');
  add(f.erevTishaBav, 'erevTishaBav');
  add(f.erevYomTov && !f.cholHaMoed, 'erevYomTov');
  add(f.erevShabbos && !f.yomTov, 'erevShabbos');
  add(f.chanukahDay, 'chanukah');
  add(f.roshChodesh && !f.yomTov, 'roshChodesh');
  add(f.aseretYemeiTeshuva && !f.erevYomKippur && !f.shabbos && !f.minorFast, 'aseretYemeiTeshuva');
  add(f.behab, 'behab');
  add(f.beinHazmanim && !f.shabbos && !f.yomTov && !f.yomKippur, 'beinHazmanim');
  if (labels.length === 0) labels.push('weekday');
  return labels;
}

/** Gold theme: Shabbos, Yom Tov and Yom Kippur. Every other day is blue. */
export function isGoldDay(date: string): boolean {
  const f = getDayFacts(date);
  return f.shabbos || f.yomTov || f.yomKippur;
}

/**
 * A Shabbos / Yom Tov stretch (spec section 10): for a gold day, the day before
 * it (`eve`, whose Mincha and evening prayers open the stretch) and every gold
 * day in a row (two days of Rosh Hashana, Yom Tov next to Shabbos). Null otherwise.
 */
export function getGoldSpan(date: string): { eve: string; days: string[] } | null {
  if (!isGoldDay(date)) return null;
  let start = date;
  while (isGoldDay(addDays(start, -1))) start = addDays(start, -1);
  const days = [start];
  while (isGoldDay(addDays(days[days.length - 1], 1))) days.push(addDays(days[days.length - 1], 1));
  return { eve: addDays(start, -1), days };
}

/** On the day before a stretch, only these prayers belong to it (from Mincha on). */
export const EVE_PRAYERS: PrayerType[] = ['mincha', 'kabalatShabbat', 'tefilahZakha', 'maariv', 'megillah', 'hakafot'];
